import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PromoBanner from '../PromoBanner';
import styles from './index.module.css';

function getSuggestionLabel(s) {
  if (typeof s === 'string') return s;
  return s.disp || s.label || s.key || '';
}

function highlightMatch(text, query) {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="flyout-search-suggestions-item-link-match">
        {text.slice(idx, idx + query.length)}
      </span>
      {text.slice(idx + query.length)}
    </>
  );
}

function useSuggestions(query, suggestUrl) {
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!query || query.length < 2 || !suggestUrl) {
      setSuggestions([]);
      return;
    }
    const separator = suggestUrl.includes('?') ? '&' : '?';
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${suggestUrl}${separator}partial_query=${encodeURIComponent(query)}`);
        const data = await res.json();
        setSuggestions(Array.isArray(data) ? data : []);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, suggestUrl]);

  return [suggestions, setSuggestions];
}

const Header = ({ content = {}, banner }) => {
  const {
    logoAltText = 'Home - Custom Plates logo',
    logoHref = '/',
    skipLinkText = 'Skip to main content',
    mobileMenuLabel = 'Menu',
    searchSuggestionEndpoint = import.meta.env.VITE_FUNNELBACK_SUGGEST_URL,
    searchResultsPage = '/search-results',
    desktopNavigation = [],
    mobileNavigation = [],
  } = content;

  const marketplaceUrl = import.meta.env.VITE_MARKETPLACE_URL || '';

  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [flyoutSearchOpen, setFlyoutSearchOpen] = useState(false);

  // Headroom: fixed header that hides on scroll-down, shows on scroll-up
  const [headroomVisible, setHeadroomVisible] = useState(true);
  const lastScrollY = React.useRef(0);
  useEffect(() => {
    const THRESHOLD = 5;
    const onScroll = () => {
      const y = window.scrollY;
      if (y <= 0) {
        setHeadroomVisible(true);
      } else if (y > lastScrollY.current + THRESHOLD) {
        setHeadroomVisible(false);
      } else if (y < lastScrollY.current - THRESHOLD) {
        setHeadroomVisible(true);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const [desktopQuery, setDesktopQuery] = useState('');
  const [desktopSuggestions, setDesktopSuggestions] = useSuggestions(desktopQuery, searchSuggestionEndpoint);

  const [mobileQuery, setMobileQuery] = useState('');
  const [mobileSuggestions, setMobileSuggestions] = useSuggestions(mobileQuery, searchSuggestionEndpoint);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const goToSearch = (term) => {
    if (!term.trim()) return;
    navigate(`${searchResultsPage}?query=${encodeURIComponent(term.trim())}&sort=relevance`);
    setDesktopQuery('');
    setDesktopSuggestions([]);
    setMobileQuery('');
    setMobileSuggestions([]);
    setSearchOpen(false);
    closeMobileMenu();
  };

  const handleDesktopSubmit = (e) => {
    e.preventDefault();
    goToSearch(desktopQuery);
  };

  const handleMobileSubmit = (e) => {
    e.preventDefault();
    goToSearch(mobileQuery);
  };

  const handleDesktopSuggestionClick = (s) => goToSearch(getSuggestionLabel(s));
  const handleMobileSuggestionClick = (s) => goToSearch(getSuggestionLabel(s));

  const handleSearchToggle = () => {
    setSearchOpen((o) => {
      if (o) {
        setDesktopQuery('');
        setDesktopSuggestions([]);
      }
      return !o;
    });
  };

  // Check if a nav item is active based on current location
  const isNavItemActive = (itemHref) => {
    if (itemHref === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(itemHref);
  };

  return (
    <header className="header header--has-sticky-child">
      <div id="header">
        <div className="row">
          <div className="component row-splitter">
            <div className={`header__main container-fluid header__main--has-headroom ${headroomVisible ? 'header__main--visible' : 'header__main--invisible'}`}>
              <div className="row">
                <div className="component header__main-container">
                  <div className="component-content">
                    <div className="header__main-logo">
                      <Link className="header__main-logo-link" to={logoHref} title="Home">
                        <img
                          className="header__main-logo-image"
                          src="/images/logos/custom-plates.svg"
                          alt={logoAltText}
                        />
                      </Link>
                      <a href="#content" className="visuallyhidden">{skipLinkText}</a>
                      <button
                        className="header__main-hamburger-link"
                        aria-expanded={mobileMenuOpen}
                        aria-controls="flyout-header"
                        type="button"
                        onClick={() => setMobileMenuOpen(true)}
                      >
                        {mobileMenuLabel}
                      </button>
                    </div>

                    <nav aria-label="Site Navigation Menu" className="header__main-nav">
                      <ul className="header__main-nav-list">
                        {desktopNavigation.map((item, index) => (
                          <li
                            key={item.href}
                            className={`header__main-nav-item level0 item${index}${isNavItemActive(item.href) ? ' active' : ''}`}
                          >
                            <div className="navigation-title">
                              <Link to={item.href}>{item.title}</Link>
                            </div>
                          </li>
                        ))}
                        {marketplaceUrl && (
                          <li className="header__main-nav-item level0">
                            <div className="navigation-title">
                              <a href={marketplaceUrl}>Marketplace</a>
                            </div>
                          </li>
                        )}
                      </ul>
                    </nav>

                    <div className="header__main-actions">
                      <button
                        className="header__main-actions-action header__main-actions-action--search"
                        aria-expanded={searchOpen}
                        aria-controls="header-main-search"
                        type="button"
                        onClick={handleSearchToggle}
                      >
                        <span className="header__main-actions-text" aria-hidden="true">Search</span>
                        <span className="header__main-actions-text header__main-actions-text--hidden" aria-hidden="true">Close</span>
                        <span className="visuallyhidden">Toggle search panel</span>
                        <span className="header__main-actions-icon header__main-actions-icon--search"></span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Search Panel */}
          <div className={`component plain-html search ${searchOpen ? '' : 'hidden'}`} id="header-main-search">
          <form className="search__form" role="search" onSubmit={handleDesktopSubmit}>
            <div className="search__input-area">
              <input
                className="search__input"
                type="text"
                id="search__term"
                aria-label="Enter your search term"
                autoComplete="off"
                placeholder="Search..."
                value={desktopQuery}
                onChange={(e) => setDesktopQuery(e.target.value)}
              />
              <button className="search__submit" type="submit">
                <span className="visuallyhidden">Search</span>
              </button>
            </div>
            <output className="search__output" htmlFor="search__term">
              {desktopSuggestions.length > 0 && (
                <ul className="search__output-list" role="listbox">
                  {desktopSuggestions.map((s, i) => (
                    <li key={i} className="search__output-item" role="option">
                      <button
                        type="button"
                        className={`search__output-link ${styles.suggestionBtn}`}
                        onMouseDown={() => handleDesktopSuggestionClick(s)}
                      >
                        {getSuggestionLabel(s)}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </output>
            {desktopQuery.trim().length >= 2 && (
              <Link
                to={`${searchResultsPage}?query=${encodeURIComponent(desktopQuery.trim())}&sort=relevance`}
                className="search__cta"
                onMouseDown={(e) => { e.preventDefault(); goToSearch(desktopQuery); }}
              >
                Search for &apos;<span className="search__cta-value">{desktopQuery}</span>&apos;
              </Link>
            )}
          </form>
          </div>

          {/* Mobile Flyout Navigation */}
          <div
            className={`flyout-nav ${mobileMenuOpen ? 'flyout-nav--active' : ''}`}
            id="flyout-header"
          >
          <div className="flyout-nav__upper-tab-point visuallyhidden" tabIndex="0"></div>
          <div
            className="flyout-nav__inner"
            role="dialog"
            tabIndex="-1"
            aria-labelledby="flyout-nav-title"
          >
            <nav>
              <div className="flyout-nav__head">
                <div className="flyout-nav__head-title" id="flyout-nav-title" aria-hidden="true">
                  Menu
                </div>
                <a
                  className="flyout-nav__close"
                  href="#"
                  onClick={(e) => { e.preventDefault(); closeMobileMenu(); }}
                >
                  <span className="visuallyhidden">Close</span>
                </a>
              </div>
              <ul className="flyout-nav__list">
                {/* Search nested flyout */}
                <li className="flyout-nav__item">
                  <a
                    className="flyout-nav__link--parent flyout-nav__link--search"
                    href="#"
                    aria-haspopup="true"
                    aria-expanded={flyoutSearchOpen}
                    onClick={(e) => { e.preventDefault(); setFlyoutSearchOpen(true); }}
                  >
                    Search
                  </a>
                  {flyoutSearchOpen && (
                    <nav className="flyout-nav flyout-nav--active">
                      <div className="flyout-nav__upper-tab-point visuallyhidden" tabIndex="0"></div>
                      <div className="flyout-nav__inner" role="dialog" tabIndex="-1" aria-labelledby="flyout-nav-title-search">
                        <div className="flyout-nav__head">
                          <a className="flyout-nav__back flyout-nav__back--search" href="#" onClick={(e) => { e.preventDefault(); setFlyoutSearchOpen(false); }}>
                            <span className="visuallyhidden">Back</span>
                          </a>
                          <div className="flyout-nav__head-title" id="flyout-nav-title-search">Search</div>
                          <a className="flyout-nav__close flyout-nav__close--search" href="#" onClick={(e) => { e.preventDefault(); closeMobileMenu(); setFlyoutSearchOpen(false); }}>
                            <span className="visuallyhidden">Close</span>
                          </a>
                        </div>
                        <form className="flyout-search__form" role="search" onSubmit={handleMobileSubmit}>
                          <div className="flyout-search__input-area">
                            <input
                              className="flyout-search__input"
                              type="text"
                              id="search__term-mobile"
                              aria-label="Enter your search term"
                              autoComplete="off"
                              value={mobileQuery}
                              onChange={(e) => setMobileQuery(e.target.value)}
                            />
                            <button
                              className="flyout-search__form-reset-button"
                              type="reset"
                              onClick={() => { setMobileQuery(''); setMobileSuggestions([]); }}
                            >
                              <span className="visuallyhidden">Clear form</span>
                            </button>
                          </div>
                          {mobileSuggestions.length > 0 && (
                            <ul className="flyout-search-suggestions" role="listbox">
                              {mobileSuggestions.map((s, i) => (
                                <li key={i} className="flyout-search-suggestions-item" role="option">
                                  <button
                                    type="button"
                                    className={`flyout-search-suggestions-item-link ${styles.suggestionBtn}`}
                                    onMouseDown={() => handleMobileSuggestionClick(s)}
                                  >
                                    {highlightMatch(getSuggestionLabel(s), mobileQuery)}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </form>
                      </div>
                      <div className="flyout-nav__lower-tab-point visuallyhidden" tabIndex="0"></div>
                    </nav>
                  )}
                </li>
                {mobileNavigation.map((item) => (
                  <li key={item.href} className="flyout-nav__item">
                    <div className="navigation-title field-navigationtitle">
                      <Link to={item.href} onClick={closeMobileMenu}>{item.title}</Link>
                    </div>
                  </li>
                ))}
                {marketplaceUrl && (
                  <li className="flyout-nav__item">
                    <div className="navigation-title field-navigationtitle">
                      <a href={marketplaceUrl} onClick={closeMobileMenu}>Marketplace</a>
                    </div>
                  </li>
                )}
              </ul>
            </nav>
          </div>
          <div className="flyout-nav__lower-tab-point visuallyhidden" tabIndex="0"></div>
        </div>
        </div>
      </div>

      {/* Promo Banner — driven by homepage JSON promoBanner component */}
      {banner && banner.isActive && banner.bannerText && (
        <PromoBanner
          content={banner.bannerText}
          linkUrl={banner.linkUrl}
          bgColor={banner.backgroundColor}
          fgColor={banner.textColor}
          buttonLabel={banner.buttonLabel}
          buttonBorderColor={banner.buttonBorderColor}
          buttonForeColor={banner.buttonTextColor}
          allowDismissal={banner.allowDismissal}
        />
      )}
    </header>
  );
};

Header.propTypes = {
  content: PropTypes.shape({
    logoAltText: PropTypes.string,
    logoHref: PropTypes.string,
    skipLinkText: PropTypes.string,
    mobileMenuLabel: PropTypes.string,
    searchSuggestionEndpoint: PropTypes.string,
    searchResultsPage: PropTypes.string,
    desktopNavigation: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    })),
    mobileNavigation: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    })),
  }),
  banner: PropTypes.shape({
    isActive: PropTypes.bool,
    bannerText: PropTypes.string,
    linkUrl: PropTypes.string,
    backgroundColor: PropTypes.string,
    textColor: PropTypes.string,
    buttonLabel: PropTypes.string,
    buttonBorderColor: PropTypes.string,
    buttonTextColor: PropTypes.string,
    allowDismissal: PropTypes.bool,
  }),
};

export default Header;
